import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FilingStatus } from 'api/StudentLoanApi';
import FormNavigation from 'components/FormNavigation';
import RadioButtonList from 'components/Common/RadioButtonList/RadioButtonList';
import NumberInput from 'components/NumberInput';
import Button from 'components/Button';
import StudentLoanLoaderWrapper from 'components/StudentLoanForgiveness/LoaderWrapper';
import { parseAmount, toInputValue } from 'components/StudentLoanForgiveness/studentLoanFormat';
import useSaveStudentLoanAnswers, { reportStudentLoanSaveFailure } from 'hooks/useSaveStudentLoanAnswers';
import { FlowComponentType } from 'routes/types';
import { getStudentLoanData } from 'selectors/getStudentLoanData';

import QuestionsWrapper from './Wrapper/QuestionsWrapper';

import styles from './Questions.module.scss';

const MAX_DEPENDENTS = 10;

const filingOptions = [
  { id: FilingStatus.Single, label: 'Single' },
  { id: FilingStatus.HeadOfHousehold, label: 'Head of household' },
  { id: FilingStatus.MarriedFilingJointly, label: 'Married, filing jointly' },
  { id: FilingStatus.MarriedFilingSeparately, label: 'Married, filing separately' },
];

const Household = ({ navigationInfo, handleNext }: FlowComponentType) => {
  const { answers } = useSelector(getStudentLoanData);
  const { saveAnswers, isSaving } = useSaveStudentLoanAnswers();

  const [filingStatus, setFilingStatus] = useState<FilingStatus | null>(answers.filingStatus ?? null);
  const [dependents, setDependents] = useState(answers.dependents ?? 0);
  const [spouseIncome, setSpouseIncome] = useState(toInputValue(answers.spouseAgi));
  const [spouseDebt, setSpouseDebt] = useState(toInputValue(answers.spouseFederalDebt));

  const isJoint = filingStatus === FilingStatus.MarriedFilingJointly;

  const handleClick = async () => {
    analytics.track('Student Loan Household Selected', { filingStatus, dependents });
    try {
      handleNext(
        await saveAnswers({
          filingStatus: filingStatus!,
          dependents,
          spouseAgi: isJoint ? parseAmount(spouseIncome) : 0,
          spouseFederalDebt: isJoint ? parseAmount(spouseDebt) : 0,
        }),
      );
    } catch {
      reportStudentLoanSaveFailure();
    }
  };

  return (
    <StudentLoanLoaderWrapper>
      <>
        <FormNavigation {...navigationInfo} />
        <QuestionsWrapper
          title="Your household"
          subtitle="Income-driven payments depend on how you file taxes and who you support."
        >
          <>
            <RadioButtonList
              label="How did you file your last federal tax return?"
              selected={filingStatus}
              setSelected={setFilingStatus}
              options={filingOptions}
            />

            <div className={styles.question}>
              <p className={styles.questionLabel}>How many dependents do you claim, not counting your spouse?</p>
              <div className={styles.dependents}>
                <button
                  type="button"
                  className={styles.dependentsButton}
                  aria-label="Fewer dependents"
                  disabled={dependents <= 0}
                  onClick={() => setDependents(dependents - 1)}
                >
                  −
                </button>
                <span className={styles.dependentsValue}>{dependents}</span>
                <button
                  type="button"
                  className={styles.dependentsButton}
                  aria-label="More dependents"
                  disabled={dependents >= MAX_DEPENDENTS}
                  onClick={() => setDependents(dependents + 1)}
                >
                  +
                </button>
              </div>
            </div>

            {isJoint && (
              <div className={styles.fields}>
                <NumberInput
                  label="Your spouse's annual income"
                  placeholder="$0"
                  name="spouseAgi"
                  prefix="$"
                  thousandSeparator
                  allowNegative={false}
                  value={spouseIncome}
                  onChange={(event) => setSpouseIncome(event.target.value)}
                />
                <NumberInput
                  label="Your spouse's federal student loan balance (0 if none)"
                  placeholder="$0"
                  name="spouseFederalDebt"
                  prefix="$"
                  thousandSeparator
                  allowNegative={false}
                  value={spouseDebt}
                  onChange={(event) => setSpouseDebt(event.target.value)}
                />
              </div>
            )}

            <Button disabled={!filingStatus} isLoading={isSaving} onClick={handleClick}>
              Next
            </Button>
          </>
        </QuestionsWrapper>
      </>
    </StudentLoanLoaderWrapper>
  );
};

export default Household;
